import {useCallback, useMemo, useState} from "react";

interface EntityWithId {
    id?: string;
}

interface PersistedSelectionState {
    storageKey: string;
    storedValue: string | null;
}

interface UsePersistentEntitySelectionInput<TEntity extends EntityWithId> {
    entities: TEntity[];
    storageKey: string;
}

export interface PersistentEntitySelection<TEntity extends EntityWithId> {
    selectedEntities: TEntity[];
    selectedIds: string[];
    selectedIdSet: ReadonlySet<string>;
    allSelected: boolean;
    setSelectedIds: (ids: readonly string[]) => void;
    toggleAll: () => void;
    toggleGroup: (ids: readonly string[]) => void;
    toggleEntity: (id: string) => void;
}

const idsEqual = (left: readonly string[], right: readonly string[]) => (
    left.length === right.length
    && right.every((id, index) => left[index] === id)
);

export function usePersistentEntitySelection<TEntity extends EntityWithId>({
    entities,
    storageKey,
}: UsePersistentEntitySelectionInput<TEntity>): PersistentEntitySelection<TEntity> {
    const [persistedSelection, setPersistedSelection] = useState<PersistedSelectionState>(() => ({
        storageKey,
        storedValue: localStorage.getItem(storageKey),
    }));
    const storedValue = persistedSelection.storageKey === storageKey
        ? persistedSelection.storedValue
        : localStorage.getItem(storageKey);
    const selectedEntities = useMemo(() => {
        if (storedValue === null) return entities;
        if (storedValue === "") return [];

        const storedIdSet = new Set(storedValue.split(","));
        return entities.filter(entity => entity.id && storedIdSet.has(entity.id));
    }, [entities, storedValue]);
    const selectedIds = useMemo(
        () => selectedEntities.map(entity => entity.id).filter((id): id is string => Boolean(id)),
        [selectedEntities],
    );
    const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
    const allEntityIds = useMemo(
        () => entities.map(entity => entity.id).filter((id): id is string => Boolean(id)),
        [entities],
    );
    const allSelected = allEntityIds.length > 0 && selectedIdSet.size === allEntityIds.length;

    const setSelectedIds = useCallback((ids: readonly string[]) => {
        const nextIdSet = new Set(ids);
        const nextIds = entities
            .filter(entity => entity.id && nextIdSet.has(entity.id))
            .map(entity => entity.id)
            .filter((id): id is string => Boolean(id));
        if (idsEqual(selectedIds, nextIds)) return;

        const storedValue = nextIds.join(",");
        localStorage.setItem(storageKey, storedValue);
        setPersistedSelection({storageKey, storedValue});
    }, [entities, selectedIds, storageKey]);

    const toggleAll = useCallback(() => {
        setSelectedIds(allSelected ? [] : allEntityIds);
    }, [allEntityIds, allSelected, setSelectedIds]);

    const toggleGroup = useCallback((ids: readonly string[]) => {
        const groupIds = new Set(ids);
        const groupSelected = ids.length > 0 && ids.every(id => selectedIdSet.has(id));
        const nextIds = groupSelected
            ? selectedIds.filter(id => !groupIds.has(id))
            : Array.from(new Set([...selectedIds, ...ids]));

        setSelectedIds(nextIds);
    }, [selectedIds, selectedIdSet, setSelectedIds]);

    const toggleEntity = useCallback((id: string) => {
        setSelectedIds(selectedIdSet.has(id)
            ? selectedIds.filter(selectedId => selectedId !== id)
            : [...selectedIds, id]);
    }, [selectedIds, selectedIdSet, setSelectedIds]);

    return {
        selectedEntities,
        selectedIds,
        selectedIdSet,
        allSelected,
        setSelectedIds,
        toggleAll,
        toggleGroup,
        toggleEntity,
    };
}
